import { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  let errorMessage: string;
  let errorStatus: number | undefined;

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorMessage = error.statusText || 'An error occurred';

    if (error.status === 404) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="w-full max-w-md rounded-lg bg-white px-6 py-8 text-center shadow-lg">
            <h1 className="text-6xl font-bold text-gray-900">404</h1>
            <p className="mt-2 text-xl text-gray-600">Page not found</p>
            <p className="mt-4 text-sm text-gray-500">
              The page you&apos;re looking for doesn&apos;t exist.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else {
    errorMessage = 'Unknown error occurred';
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md rounded-lg bg-white px-6 py-8 shadow-lg">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-yellow-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 className="mt-4 text-xl font-bold text-gray-900">
            {errorStatus
              ? `Error ${errorStatus}`
              : 'Oops! Something went wrong'}
          </h1>
          <p className="mt-2 text-sm text-gray-600">{errorMessage}</p>
          {import.meta.env.DEV && error instanceof Error && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Stack Trace (Development Only)
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs">
                {error.stack}
              </pre>
            </details>
          )}
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
            >
              Refresh Page
            </button>
            <button
              onClick={() => navigate('/')}
              className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AsyncBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface AsyncBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class AsyncErrorBoundary extends Component<
  AsyncBoundaryProps,
  AsyncBoundaryState
> {
  constructor(props: AsyncBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AsyncBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Async Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">
            Failed to load this section. Please try refreshing.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
